// Implements: F-UIX-018, F-USG-008, F-UIX-010
/**
 * "Distribución" (UIX.6): the organization admin distributes the organization's budget within
 * the ceiling, and a team lead reads its teams' allocations.
 *
 * `DistributionByOrganization` offers the organizations the session grants
 * (`distributionTargetsOf`) and mounts one `DistributionContainer` keyed by the organization's
 * id (checklist 14): switching organization mounts a fresh one, so no typed value, row message
 * or late answer of the previous organization reaches the next one's screen (the reads and the
 * writes' answers are keyed by the organization's id as well).
 *
 * The server decides everything that matters: which view a reader gets (the whole organization
 * or the led teams), whether the controls exist (`can_manage`), every bound on submit (its
 * refusal is shown in the row). A refused first read is an error state with its retry (F-UIX-010);
 * a failed re-read keeps what is on screen.
 * No browser storage.
 */

import type { UseQueryResult } from "@tanstack/react-query";
import { useId, useMemo, useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import type { BackofficeScope } from "../../../types/api";
import { OrganizationDistribution, TeamDistribution } from "../components/DistributionEditor";
import type { DistributionWriteActions } from "../components/DistributionEditor";
import styles from "../components/MyUsage.module.css";
import { useDistribution, useDistributionWrites } from "../hooks/useDistribution";
import { distributionTargetsOf, sessionTeamNamesOf } from "../model/distribution";
import type { DistributionTarget } from "../model/distribution";

type Translate = ReturnType<typeof useTranslation>["t"];

function targetLabel(target: DistributionTarget, t: Translate): string {
  return target.name === null
    ? t("backoffice.distribution.target.unnamed")
    : t("backoffice.distribution.target.organization", { name: target.name });
}

/**
 * A read's error state with its retry, its loading state, or `render`.
 *
 * The error state replaces the section only while there is nothing to show: once the view was
 * read, a failed re-read (after a save, say) keeps the rows and what is being typed in them, with
 * a quiet note that the view could not be refreshed.
 */
function Section<T>({
  read,
  render,
}: {
  readonly read: UseQueryResult<T, ApiError>;
  readonly render: (data: T) => ReactElement;
}): ReactElement {
  const { t } = useTranslation();
  if (read.data === undefined) {
    if (read.isError) {
      return (
        <ErrorState
          error={asApiError(read.error)}
          onRetry={() => {
            void read.refetch();
          }}
        />
      );
    }
    return <LoadingState label={t("backoffice.distribution.loading")} />;
  }
  // One shape whether or not the note shows, so the rows below it are never remounted.
  return (
    <>
      {read.isError ? <p className={styles.hint}>{t("backoffice.distribution.stale")}</p> : null}
      {render(read.data)}
    </>
  );
}

export interface DistributionContainerProps {
  readonly target: DistributionTarget;
  /** The teams the session carries in this organization: how a team lead's view names them. */
  readonly sessionTeamNames: ReadonlyMap<string, string>;
}

/** One organization's distribution. Mount it keyed by the organization's id. */
export function DistributionContainer({ target, sessionTeamNames }: DistributionContainerProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const { policy, people, teams } = useDistribution(target.organizationId);
  const writes = useDistributionWrites(target.organizationId);
  const actions: DistributionWriteActions = {
    setPersonLimit: writes.setPersonLimit,
    clearPersonLimit: writes.clearPersonLimit,
    setTeamAllocation: (teamId, monthlyBudgetMicros) =>
      writes.setTeamAllocation(teamId, { monthly_budget_micros: monthlyBudgetMicros }),
    clearTeamAllocation: writes.clearTeamAllocation,
  };

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.sectionTitle}>
        {targetLabel(target, t)}
      </h2>
      <Section
        read={policy}
        render={(view) =>
          view.view === "team" ? (
            <TeamDistribution policy={view} teamNames={sessionTeamNames} />
          ) : (
            <Section
              read={people}
              render={(listed) => (
                <Section
                  read={teams}
                  render={(list) => (
                    <OrganizationDistribution
                      policy={view}
                      people={listed.people}
                      teamNames={new Map(list.teams.map((team) => [team.id, team.name] as const))}
                      actions={actions}
                    />
                  )}
                />
              )}
            />
          )
        }
      />
    </section>
  );
}

export interface DistributionByOrganizationProps {
  /** The session's scopes: what the person administers or leads. */
  readonly scopes: readonly BackofficeScope[];
}

export function DistributionByOrganization({ scopes }: DistributionByOrganizationProps): ReactElement {
  const { t } = useTranslation();
  const pickerId = useId();
  const targets = useMemo(() => distributionTargetsOf(scopes), [scopes]);
  const [chosen, setChosen] = useState<string | null>(null);
  const target = targets.find((candidate) => candidate.organizationId === chosen) ?? targets[0];
  const sessionTeamNames = useMemo(
    () => (target === undefined ? new Map<string, string>() : sessionTeamNamesOf(scopes, target.organizationId)),
    [scopes, target],
  );

  if (target === undefined) {
    return <EmptyState title={t("backoffice.distribution.none.title")} body={t("backoffice.distribution.none.body")} />;
  }

  return (
    <div className={styles.section}>
      {targets.length > 1 ? (
        <p>
          <label htmlFor={pickerId}>{t("backoffice.distribution.target.label")}</label>{" "}
          <select
            id={pickerId}
            value={target.organizationId}
            onChange={(event) => {
              setChosen(event.target.value);
            }}
          >
            {targets.map((candidate) => (
              <option key={candidate.organizationId} value={candidate.organizationId}>
                {targetLabel(candidate, t)}
              </option>
            ))}
          </select>
        </p>
      ) : null}
      <DistributionContainer key={target.organizationId} target={target} sessionTeamNames={sessionTeamNames} />
    </div>
  );
}
